import { useEffect, useState, useMemo, Fragment } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Search, ShoppingCart, Package, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Date helpers for the shortcut chips
const iso = (d) => d.toISOString().slice(0, 10);
const RANGE_SHORTCUTS = [
  { key: "this_month", label: "This month", compute: () => {
      const now = new Date();
      return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
    } },
  { key: "last_month", label: "Last month", compute: () => {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return [iso(first), iso(last)];
    } },
  { key: "last_30", label: "Last 30 days", compute: () => {
      const now = new Date();
      const s = new Date(); s.setDate(now.getDate() - 30);
      return [iso(s), iso(now)];
    } },
  { key: "last_90", label: "Last 90 days", compute: () => {
      const now = new Date();
      const s = new Date(); s.setDate(now.getDate() - 90);
      return [iso(s), iso(now)];
    } },
  { key: "ytd", label: "Year to date", compute: () => {
      const now = new Date();
      return [iso(new Date(now.getFullYear(), 0, 1)), iso(now)];
    } },
  { key: "last_year", label: "Last year", compute: () => {
      const now = new Date();
      const y = now.getFullYear() - 1;
      return [`${y}-01-01`, `${y}-12-31`];
    } },
  { key: "last_12", label: "Last 12 months", compute: () => {
      const now = new Date();
      const s = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
      return [iso(s), iso(now)];
    } },
];

export const HowMuchDidIBuyTab = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierQuery, setSupplierQuery] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [bucket, setBucket] = useState("monthly");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const applyShortcut = (sc) => {
    const [f, t] = sc.compute();
    setFromDate(f);
    setToDate(t);
  };

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Load supplier list once for the picker
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/suppliers`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.suppliers || []);
        setSuppliers(list.filter((s) => s.id));
      } catch (e) {
        toast.error("Could not load suppliers");
      }
    })();
  }, []);

  const filteredSuppliers = useMemo(() => {
    const q = supplierQuery.trim().toLowerCase();
    if (!q) return suppliers.slice(0, 40);
    return suppliers
      .filter((s) => (s.name || "").toLowerCase().includes(q))
      .slice(0, 40);
  }, [suppliers, supplierQuery]);

  const runReport = async (supplier) => {
    const sup = supplier || selectedSupplier;
    if (!sup?.id) {
      toast.warning("Pick a supplier first");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ bucket });
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const res = await axios.get(`${API}/suppliers/${sup.id}/purchases-buckets?${params.toString()}`);
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load purchase totals");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="how-much-did-i-buy-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-emerald-600" />
            How Much Did I Buy?
          </CardTitle>
          <p className="text-sm text-gray-500">
            Pick a supplier and see your GRV total (RAND, incl VAT) grouped by week, month or year.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Supplier picker */}
          <div>
            <Label className="text-xs mb-1 block">Supplier</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              <Input
                value={selectedSupplier ? selectedSupplier.name : supplierQuery}
                onChange={(e) => { setSelectedSupplier(null); setSupplierQuery(e.target.value); }}
                placeholder="Type supplier name..."
                className="pl-10"
                data-testid="hmdib-supplier-search"
              />
            </div>
            {!selectedSupplier && supplierQuery && (
              <div className="mt-2 max-h-48 overflow-y-auto border rounded bg-white shadow-sm divide-y">
                {filteredSuppliers.length === 0 && (
                  <div className="p-3 text-sm text-gray-500">No matching suppliers</div>
                )}
                {filteredSuppliers.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => { setSelectedSupplier(s); setSupplierQuery(""); }}
                    className="p-3 hover:bg-emerald-50 cursor-pointer text-sm"
                    data-testid={`hmdib-supplier-option-${s.id}`}
                  >
                    {s.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Date shortcuts */}
          <div>
            <Label className="text-xs mb-1 block">Quick date range</Label>
            <div className="flex flex-wrap gap-2" data-testid="hmdib-shortcuts">
              {RANGE_SHORTCUTS.map((sc) => (
                <Button
                  key={sc.key}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyShortcut(sc)}
                  data-testid={`hmdib-shortcut-${sc.key}`}
                  className="text-xs"
                >
                  {sc.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Bucket + dates */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Group by</Label>
              <Select value={bucket} onValueChange={setBucket}>
                <SelectTrigger data-testid="hmdib-bucket">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Week</SelectItem>
                  <SelectItem value="monthly">Month</SelectItem>
                  <SelectItem value="yearly">Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="hmdib-from" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="hmdib-to" />
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => runReport()}
                disabled={loading || !selectedSupplier}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="hmdib-run"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
                Show me
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{result.supplier?.name}</span>
              <span className="text-emerald-700 text-2xl" data-testid="hmdib-grand-total">
                {money(result.grand_total_incl_vat)}
              </span>
            </CardTitle>
            <p className="text-sm text-gray-500">
              {result.grv_count} GRV{result.grv_count === 1 ? "" : "s"} •{" "}
              {result.bucket === "weekly" ? "Weekly" : result.bucket === "yearly" ? "Yearly" : "Monthly"} •{" "}
              {result.from_date} → {result.to_date}
            </p>
          </CardHeader>
          <CardContent>
            {result.buckets?.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No completed GRVs from this supplier in the selected range.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead className="text-right">GRVs</TableHead>
                    <TableHead className="text-right">Total (incl VAT)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.buckets.map((b) => {
                    const isOpen = expandedKeys.has(b.key);
                    return (
                      <Fragment key={b.key}>
                        <TableRow
                          data-testid={`hmdib-row-${b.key}`}
                          onClick={() => toggleExpanded(b.key)}
                          className="cursor-pointer hover:bg-emerald-50"
                        >
                          <TableCell className="w-8">
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{b.label}</TableCell>
                          <TableCell className="text-gray-500 text-sm">{b.period_start}</TableCell>
                          <TableCell className="text-gray-500 text-sm">{b.period_end}</TableCell>
                          <TableCell className="text-right">{b.grv_count}</TableCell>
                          <TableCell className="text-right font-semibold text-emerald-700">
                            {money(b.total_incl_vat)}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow data-testid={`hmdib-drill-${b.key}`}>
                            <TableCell colSpan={6} className="bg-emerald-50/60 p-0">
                              <div className="p-4">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="w-32">GRV #</TableHead>
                                      <TableHead className="w-40">Supplier Invoice #</TableHead>
                                      <TableHead className="w-32">Received</TableHead>
                                      <TableHead className="text-right">Total (incl VAT)</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {(b.grvs || []).map((g, idx) => (
                                      <TableRow key={`${b.key}-${g.grv_number}-${idx}`}>
                                        <TableCell className="font-mono text-sm">{g.grv_number || "—"}</TableCell>
                                        <TableCell className="font-mono text-sm">{g.invoice_number || "—"}</TableCell>
                                        <TableCell className="text-sm">{g.received_date}</TableCell>
                                        <TableCell className="text-right font-semibold">
                                          {money(g.total_incl_vat)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HowMuchDidIBuyTab;
